import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { membershipAPI } from '../lib/api';
import { useAuth } from './AuthContext';

const MembershipContext = createContext();

export function MembershipProvider({ children }) {
    const { isAuthenticated } = useAuth();
    const [membership, setMembership] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showUpgradeModal, setShowUpgradeModal] = useState(false);
    const [dismissedForSession, setDismissedForSession] = useState(false);
    const timerRef = useRef(null);
    const trackingRef = useRef(null);
    const expirationCheckRef = useRef(null);

    // Fetch membership status
    const fetchMembership = useCallback(async () => {
        if (!isAuthenticated) {
            setMembership(null);
            setLoading(false);
            return;
        }

        try {
            const response = await membershipAPI.getStatus();
            setMembership(response.data);
            
            // Show upgrade modal if expired and not dismissed this session
            if (response.data.status === 'expired' && !dismissedForSession) {
                setShowUpgradeModal(true);
            }
        } catch (err) {
            console.error('Failed to fetch membership:', err);
        } finally {
            setLoading(false);
        }
    }, [isAuthenticated, dismissedForSession]);

    // Track time usage (call every 30 seconds when active)
    const trackTime = useCallback(async () => {
        if (!isAuthenticated || !membership?.is_active || membership?.is_grace_period) {
            return;
        }

        // Only track for free trial users
        if (membership?.status !== 'free_trial') {
            return;
        }

        try {
            const response = await membershipAPI.trackTime(30); // 30 seconds
            setMembership(response.data);
            
            if (response.data.status === 'expired' && !dismissedForSession) {
                setShowUpgradeModal(true);
            }
        } catch (err) {
            console.error('Failed to track time:', err);
        }
    }, [isAuthenticated, membership?.is_active, membership?.is_grace_period, membership?.status, dismissedForSession]);

    // Initialize and start tracking
    useEffect(() => {
        fetchMembership();
    }, [fetchMembership]);

    // Start time tracking interval for free trial users
    useEffect(() => {
        if (membership?.status === 'free_trial' && membership?.is_active) {
            trackingRef.current = setInterval(trackTime, 30000); // Every 30 seconds
        }

        return () => {
            if (trackingRef.current) {
                clearInterval(trackingRef.current);
            }
        };
    }, [membership?.status, membership?.is_active, trackTime]);

    // Also check inside expiration check interval
    useEffect(() => {
        if (expirationCheckRef.current) {
            clearInterval(expirationCheckRef.current);
        }

        if (membership?.status === 'active' && membership?.expires_at) {
            const checkExpiration = () => {
                const now = new Date();
                const expiryDate = new Date(membership.expires_at);
                
                if (now >= expiryDate) {
                    console.log('Membership expired! Showing upgrade modal...');
                    if (!dismissedForSession) setShowUpgradeModal(true);
                    fetchMembership();
                    if (expirationCheckRef.current) {
                        clearInterval(expirationCheckRef.current);
                    }
                }
            };

            expirationCheckRef.current = setInterval(checkExpiration, 1000);
            checkExpiration();
        }

        return () => {
            if (expirationCheckRef.current) {
                clearInterval(expirationCheckRef.current);
            }
        };
    }, [membership?.status, membership?.expires_at, fetchMembership, dismissedForSession]);

    // Force show upgrade modal when membership is expired (only if not dismissed)
    useEffect(() => {
        if (membership?.status === 'expired' && !showUpgradeModal && !dismissedForSession) {
            setShowUpgradeModal(true);
        }
    }, [membership?.status, showUpgradeModal, dismissedForSession]);

    // Format time remaining
    const formatTimeRemaining = (seconds) => {
        if (!seconds || seconds <= 0) return '0:00';
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        const secs = seconds % 60;
        
        if (hours > 0) {
            return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
        }
        return `${minutes}:${secs.toString().padStart(2, '0')}`;
    };

    // Dismiss modal for this session (user can browse but not use premium features)
    const dismissModal = () => {
        setDismissedForSession(true);
        setShowUpgradeModal(false);
    };

    // Check if user can access app
    const canAccess = membership?.is_active || membership?.is_grace_period;

    const value = {
        membership,
        loading,
        canAccess,
        showUpgradeModal,
        setShowUpgradeModal,
        dismissModal,
        refreshMembership: fetchMembership,
        formatTimeRemaining,
        isGracePeriod: membership?.is_grace_period,
        isFreeTrial: membership?.status === 'free_trial',
        isActive: membership?.status === 'active',
        isExpired: membership?.status === 'expired',
        timeRemaining: membership?.time_remaining_seconds,
    };

    return (
        <MembershipContext.Provider value={value}>
            {children}
        </MembershipContext.Provider>
    );
}

export function useMembership() {
    const context = useContext(MembershipContext);
    if (!context) {
        throw new Error('useMembership must be used within a MembershipProvider');
    }
    return context;
}
